import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { MapPin, X, Search, Navigation, Home, Briefcase, Star, ChevronDown, ChevronUp, Loader2, Check, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUserProfileOptional, type SavedAddress } from "@/hooks/useUserProfile";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

interface GooglePlaceResult {
  placeId: string;
  description: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  latitude: number;
  longitude: number;
}

interface SavedLocation {
  id: string;
  name: string;
  address: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  isPrimary?: boolean;
  label?: string;
}

interface LocationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect: (location: SavedLocation | 'current') => void;
  selectedLocation?: string;
}

function getCachedAddressesForUser(userId: string | number | null): SavedAddress[] {
  if (!userId) return [];
  try {
    const cacheKey = `user_addresses_cache_${userId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.error('[LocationSelectionModal] Error reading cached addresses:', e);
  }
  return [];
}

function convertAddressToLocation(address: SavedAddress): SavedLocation {
  const fullAddress = [
    address.streetAddress,
    address.apartment,
    address.city,
    address.state,
    address.zipCode
  ].filter(Boolean).join(', ');
  
  return {
    id: `eatzy-address-${address.id}`,
    name: address.label || `${address.streetAddress}, ${address.city}`,
    address: fullAddress,
    coordinates: address.latitude && address.longitude 
      ? { lat: address.latitude, lng: address.longitude }
      : undefined,
    isPrimary: address.isPrimary,
    label: address.label,
  };
}

function getLabelIcon(label?: string) {
  if (!label) return MapPin;
  const lowerLabel = label.toLowerCase();
  if (lowerLabel.includes('home')) return Home;
  if (lowerLabel.includes('work') || lowerLabel.includes('office')) return Briefcase;
  return MapPin;
}

const LABEL_PRESETS = [
  { value: 'Home', icon: Home },
  { value: 'Work', icon: Briefcase },
  { value: 'Other', icon: MapPin },
];

export default function LocationSelectionModal({
  isOpen,
  onClose,
  onLocationSelect,
  selectedLocation = "Select location"
}: LocationSelectionModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string>("");
  
  const [autocompleteResults, setAutocompleteResults] = useState<GooglePlaceResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const [selectedPlace, setSelectedPlace] = useState<GooglePlaceResult | null>(null);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [showExtraDetails, setShowExtraDetails] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveFormData, setSaveFormData] = useState({
    label: 'Home',
    customLabel: '',
    apartment: '',
    businessName: '',
    doorCode: '',
    deliveryNotes: '',
  });
  
  const { isAuthenticated, user: authUser } = useAuth();
  const userProfile = useUserProfileOptional();
  const { toast } = useToast();
  
  const currentUserId = authUser?.id || authUser?.email || null;
  
  const savedLocations = useMemo(() => {
    if (isAuthenticated && userProfile?.addresses && userProfile.addresses.length > 0) {
      return userProfile.addresses.map(convertAddressToLocation);
    }
    if (isAuthenticated && currentUserId) {
      const cachedAddresses = getCachedAddressesForUser(currentUserId);
      if (cachedAddresses.length > 0) {
        return cachedAddresses.map(convertAddressToLocation);
      }
    }
    return [];
  }, [isAuthenticated, userProfile?.addresses, currentUserId]);

  const filteredLocations = useMemo(() => {
    if (searchQuery.trim() && autocompleteResults.length === 0 && !isSearching) {
      return savedLocations.filter((location: SavedLocation) =>
        location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        location.address.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (!searchQuery.trim()) {
      return savedLocations;
    }
    return [];
  }, [searchQuery, savedLocations, autocompleteResults.length, isSearching]);

  const fetchAutocomplete = useCallback(async (query: string) => {
    if (query.trim().length < 3) {
      setAutocompleteResults([]);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    try {
      const params = new URLSearchParams({ q: query });
      const response = await fetch(`/api/address/autocomplete?${params}`);
      if (response.ok) {
        const data = await response.json();
        setAutocompleteResults(data.results || []);
      }
    } catch (err) {
      console.error('[LocationSelectionModal] Autocomplete error:', err);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    if (searchQuery.trim().length < 3) {
      setAutocompleteResults([]);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchAutocomplete(searchQuery);
    }, 350);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, fetchAutocomplete]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setAutocompleteResults([]);
      setSelectedPlace(null);
      setShowSaveForm(false);
      setShowExtraDetails(false);
      setSaveSuccess(false);
      setSaveFormData({
        label: 'Home',
        customLabel: '',
        apartment: '',
        businessName: '',
        doorCode: '',
        deliveryNotes: '',
      });
    }
  }, [isOpen]);

  const handleUseCurrentLocation = async () => {
    setIsRequestingLocation(true);
    setLocationError("");
    
    try {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            onLocationSelect('current');
            setIsRequestingLocation(false);
          },
          (error) => {
            setIsRequestingLocation(false);
            let errorMessage = "Failed to get location";
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = "Location permission denied. Please enable location access.";
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage = "Location information unavailable.";
                break;
              case error.TIMEOUT:
                errorMessage = "Location request timed out.";
                break;
            }
            setLocationError(errorMessage);
          }
        );
      } else {
        setIsRequestingLocation(false);
        setLocationError("Geolocation is not supported by this browser.");
      }
    } catch (error) {
      setIsRequestingLocation(false);
      setLocationError("Failed to request location permission.");
    }
  };

  const handleLocationSelect = (location: SavedLocation) => {
    onLocationSelect(location);
  };

  const handlePlaceSelect = (place: GooglePlaceResult) => {
    setSelectedPlace(place);
    setSearchQuery("");
    setAutocompleteResults([]);
    
    if (isAuthenticated) {
      setShowSaveForm(true);
    } else {
      const location: SavedLocation = {
        id: `google-${place.placeId}`,
        name: place.streetAddress || place.description,
        address: place.description,
        coordinates: { lat: place.latitude, lng: place.longitude },
      };
      onLocationSelect(location);
    }
  };

  const handleUseWithoutSaving = () => {
    if (!selectedPlace) return;
    const location: SavedLocation = {
      id: `google-${selectedPlace.placeId}`,
      name: selectedPlace.streetAddress || selectedPlace.description,
      address: selectedPlace.description,
      coordinates: { lat: selectedPlace.latitude, lng: selectedPlace.longitude },
    };
    onLocationSelect(location);
  };

  const handleSaveAddress = async () => {
    if (!selectedPlace || !userProfile?.addAddress) return;
    
    if (!selectedPlace.streetAddress && !selectedPlace.description) {
      toast({
        title: "Incomplete address",
        description: "This address doesn't have enough detail to save. Try a more specific address.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSaving(true);
    try {
      const label = saveFormData.label === 'Other' && saveFormData.customLabel.trim()
        ? saveFormData.customLabel.trim()
        : saveFormData.label;

      const newAddress: Omit<SavedAddress, 'id'> = {
        label,
        streetAddress: selectedPlace.streetAddress || selectedPlace.description,
        apartment: saveFormData.apartment || undefined,
        city: selectedPlace.city,
        state: selectedPlace.state,
        zipCode: selectedPlace.zipCode,
        country: selectedPlace.country || 'US',
        latitude: selectedPlace.latitude,
        longitude: selectedPlace.longitude,
        isPrimary: (userProfile.addresses?.length || 0) === 0,
        deliveryInstructions: saveFormData.deliveryNotes || undefined,
        placeId: selectedPlace.placeId,
        doorCode: saveFormData.doorCode || undefined,
        businessName: saveFormData.businessName || undefined,
      };

      const saved = await userProfile.addAddress(newAddress);
      if (saved) {
        setSaveSuccess(true);
        toast({
          title: "Address saved",
          description: `"${label}" has been added to your profile.`,
        });
        
        setTimeout(() => {
          const location: SavedLocation = {
            id: `eatzy-address-${saved.id}`,
            name: label,
            address: selectedPlace.description,
            coordinates: { lat: selectedPlace.latitude, lng: selectedPlace.longitude },
            isPrimary: newAddress.isPrimary,
            label,
          };
          onLocationSelect(location);
        }, 800);
      } else {
        toast({
          title: "Couldn't save address",
          description: "There was a problem saving the address. You can still use it for now.",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error('[LocationSelectionModal] Save address error:', err);
      toast({
        title: "Error saving address",
        description: "Something went wrong. Try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isUsingRealAddresses = isAuthenticated && userProfile?.addresses && userProfile.addresses.length > 0;

  if (selectedPlace && showSaveForm) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="sm:max-w-md mx-auto flex flex-col max-h-[85svh]" data-testid="location-save-modal">
          <DialogHeader className="flex-shrink-0 pb-2">
            <DialogTitle className="text-lg font-semibold" data-testid="save-address-title">
              Save this address?
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border">
              <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{selectedPlace.streetAddress}</div>
                <div className="text-xs text-muted-foreground">{selectedPlace.description}</div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium mb-2 block">Label</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  {LABEL_PRESETS.map(preset => {
                    const isActive = saveFormData.label === preset.value;
                    return (
                      <Button
                        key={preset.value}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSaveFormData(prev => ({ ...prev, label: preset.value }))}
                        className="gap-1.5"
                        data-testid={`label-${preset.value.toLowerCase()}`}
                      >
                        <preset.icon className="w-3.5 h-3.5" />
                        {preset.value}
                      </Button>
                    );
                  })}
                </div>
                {saveFormData.label === 'Other' && (
                  <Input
                    placeholder="Custom label (e.g., Gym, Mom's house)"
                    value={saveFormData.customLabel}
                    onChange={(e) => setSaveFormData(prev => ({ ...prev, customLabel: e.target.value }))}
                    className="mt-2"
                    data-testid="input-custom-label"
                    autoFocus
                  />
                )}
              </div>

              <div>
                <Label htmlFor="apartment" className="text-sm font-medium">Apt / Suite / Unit</Label>
                <Input
                  id="apartment"
                  placeholder="e.g., Apt 4B, Suite 200"
                  value={saveFormData.apartment}
                  onChange={(e) => setSaveFormData(prev => ({ ...prev, apartment: e.target.value }))}
                  className="mt-1"
                  data-testid="input-apartment"
                />
              </div>

              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowExtraDetails(!showExtraDetails)}
                  className="gap-1.5 text-muted-foreground px-0"
                  data-testid="button-toggle-details"
                >
                  {showExtraDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {showExtraDetails ? 'Less details' : 'Add details (door code, notes...)'}
                </Button>
                
                {showExtraDetails && (
                  <div className="space-y-3 mt-2">
                    <div>
                      <Label htmlFor="businessName" className="text-sm font-medium">Business name</Label>
                      <Input
                        id="businessName"
                        placeholder="Optional"
                        value={saveFormData.businessName}
                        onChange={(e) => setSaveFormData(prev => ({ ...prev, businessName: e.target.value }))}
                        className="mt-1"
                        data-testid="input-business-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="doorCode" className="text-sm font-medium">Door / Gate code</Label>
                      <Input
                        id="doorCode"
                        placeholder="Optional"
                        value={saveFormData.doorCode}
                        onChange={(e) => setSaveFormData(prev => ({ ...prev, doorCode: e.target.value }))}
                        className="mt-1"
                        data-testid="input-door-code"
                      />
                    </div>
                    <div>
                      <Label htmlFor="deliveryNotes" className="text-sm font-medium">Delivery notes</Label>
                      <Textarea
                        id="deliveryNotes"
                        placeholder="e.g., Leave at the side door, ring buzzer 2"
                        value={saveFormData.deliveryNotes}
                        onChange={(e) => setSaveFormData(prev => ({ ...prev, deliveryNotes: e.target.value }))}
                        className="mt-1 resize-none"
                        rows={2}
                        data-testid="input-delivery-notes"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-3 border-t flex-shrink-0">
            {saveSuccess ? (
              <div className="flex items-center justify-center gap-2 py-2 text-sm font-medium text-green-600 dark:text-green-400">
                <Check className="w-4 h-4" />
                Address saved
              </div>
            ) : (
              <>
                <Button
                  onClick={handleSaveAddress}
                  disabled={isSaving || (saveFormData.label === 'Other' && !saveFormData.customLabel.trim())}
                  className="w-full"
                  data-testid="button-save-address"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save address"
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleUseWithoutSaving}
                  className="w-full text-muted-foreground"
                  data-testid="button-use-without-saving"
                >
                  Use without saving
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md mx-auto max-h-[80svh] flex flex-col" data-testid="location-modal">
        <DialogHeader className="flex-shrink-0 pb-2">
          <div className="text-sm text-muted-foreground">Selected location</div>
          <DialogTitle className="text-xl font-semibold" data-testid="location-modal-title">
            {selectedLocation}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col space-y-3 overflow-hidden">
          <div className="relative flex-shrink-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Type an address to search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4"
              data-testid="location-search-input"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
            )}
          </div>

          {autocompleteResults.length > 0 && (
            <div className="space-y-1 flex-shrink-0 max-h-[240px] overflow-y-auto" data-testid="autocomplete-results">
              <div className="text-xs font-medium text-muted-foreground px-1 mb-1">Search results</div>
              {autocompleteResults.map((place) => (
                <div
                  key={place.placeId}
                  onClick={() => handlePlaceSelect(place)}
                  className="flex items-start gap-3 p-3 rounded-lg border hover-elevate cursor-pointer"
                  data-testid={`place-${place.placeId}`}
                >
                  <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg flex-shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{place.streetAddress || place.description.split(',')[0]}</div>
                    <div className="text-xs text-muted-foreground truncate">{place.description}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!autocompleteResults.length && (
            <>
              <Button
                variant="outline"
                onClick={handleUseCurrentLocation}
                disabled={isRequestingLocation}
                className="flex items-center gap-3 p-4 h-auto justify-start flex-shrink-0"
                data-testid="button-use-current-location"
              >
                <div className="flex items-center justify-center w-8 h-8 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
                  <Navigation className="w-4 h-4 text-orange-600" />
                </div>
                <span className="font-medium">
                  {isRequestingLocation ? 'Getting location...' : 'Use Current Location'}
                </span>
              </Button>

              {locationError && (
                <div 
                  className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg flex-shrink-0"
                  data-testid="status-location-error"
                >
                  <X className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">{locationError}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setLocationError("")}
                    className="ml-auto h-auto p-1"
                    data-testid="button-dismiss-error"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}

              <div className="flex-1 overflow-y-auto space-y-2">
                {savedLocations.length > 0 && (
                  <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                    <div className="text-sm font-medium text-muted-foreground">
                      {isUsingRealAddresses ? 'Your saved addresses' : 'Saved locations'}
                    </div>
                    {isUsingRealAddresses && (
                      <Badge variant="secondary" className="text-xs">
                        From your account
                      </Badge>
                    )}
                  </div>
                )}
                
                {userProfile?.isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <>
                    {filteredLocations.map((location: SavedLocation) => {
                      const IconComponent = getLabelIcon(location.label);
                      return (
                        <div
                          key={location.id}
                          onClick={() => handleLocationSelect(location)}
                          className="flex items-center justify-between gap-2 p-3 rounded-lg border hover-elevate cursor-pointer group"
                          data-testid={`location-${location.id}`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex items-center justify-center w-8 h-8 bg-muted rounded-lg flex-shrink-0">
                              <IconComponent className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-medium text-sm truncate">{location.name}</div>
                                {location.isPrimary && (
                                  <Badge variant="outline" className="text-xs flex items-center gap-1">
                                    <Star className="w-3 h-3" />
                                    Primary
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">{location.address}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {filteredLocations.length === 0 && !searchQuery.trim() && !isAuthenticated && (
                      <div className="text-center py-8 text-muted-foreground">
                        <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="mb-2">Sign in to see your saved addresses</p>
                        <p className="text-xs">Your delivery addresses will appear here</p>
                      </div>
                    )}

                    {filteredLocations.length === 0 && !searchQuery.trim() && isAuthenticated && (
                      <div className="text-center py-8 text-muted-foreground">
                        <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="mb-2">No saved addresses yet</p>
                        <p className="text-xs">Search for an address above to add one</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
